import React from "react";
import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  ChatText,
  Check,
  CheckAll,
  ExclamationTriangle,
  Eye,
  X,
} from "react-bootstrap-icons";
import { Article } from "../../models/responses/article";
import { ArticleType } from "../../models/enums/articleTypeEnum";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { Supplier } from "../../models/responses/supplier";
import { OrderPageType } from "../../models/responses/orderPageType";

interface ArticlesTableProps {
  articles: Article[];
  allProductVerified: boolean;
  allTimeVerified: boolean;
  showStockInfo: boolean;
  articleStockStatus: Record<string, any>;
  orderPageType: OrderPageType | null;
  clientType: ClientType;
  suppliers: Supplier[];
  orderSupplierId: string | null;
  onArticleClick: (article: Article) => void;
}

const getStockStatusMessage = (status: any): string => {
  if (!status) return "";
  if (typeof status === "object") {
    if (status.error) return "Fel vid hämtning";
    if (status.message) return status.message;
    return JSON.stringify(status);
  }
  if (typeof status === "string") return status;
  return "";
};

const ArticlesTable: React.FC<ArticlesTableProps> = ({
  articles,
  allProductVerified,
  allTimeVerified,
  showStockInfo,
  articleStockStatus,
  orderPageType,
  clientType,
  suppliers,
  orderSupplierId,
  onArticleClick,
}) => {
  return (
    <>
      <h3>Artiklar</h3>
      <Table responsive striped bordered hover>
        <thead>
          <tr key="-1">
            <th>
              {allProductVerified ? (
                <CheckAll className="text-success" />
              ) : (
                <ExclamationTriangle className="text-danger" />
              )}
            </th>
            {showStockInfo &&
              articles.some(
                (article) =>
                  article.id &&
                  getStockStatusMessage(articleStockStatus[article.id]),
              ) && <th>Info</th>}
            {orderPageType && orderPageType.articleTableArticleNumber && (
              <th>Art.nr</th>
            )}
            {orderPageType && orderPageType.articleTableDescription && (
              <th>Beskrivning</th>
            )}
            {orderPageType && orderPageType.articleTableQuantity && (
              <th>Antal</th>
            )}
            {clientType === ClientType.Fleet && <th>Saldo</th>}
            {orderPageType && orderPageType.articleTableSalesPrice && (
              <th>Försäljningspris</th>
            )}
            {orderPageType &&
              orderPageType.articleTableDiscount &&
              (clientType === ClientType.Standard ||
                clientType === ClientType.Fleet) && <th>Rabatt (%)</th>}
            {orderPageType && orderPageType.articleTablePurchasePrice && (
              <th>Inköpspris</th>
            )}
            {(clientType === ClientType.Catalog ||
              (orderPageType && orderPageType.articleTableSupplierName)) && (
              <th>Leverantör</th>
            )}
            {orderPageType && orderPageType.articleTableIONumber && (
              <th>IO-nummer</th>
            )}
            {orderPageType && orderPageType.articleTableComment && (
              <th>Extern kommentar</th>
            )}
            {clientType === ClientType.Fleet && (
              <>
                <th>Exkluderad från order</th>
              </>
            )}
            {orderPageType &&
              orderPageType.articleTablePartUrl &&
              clientType === ClientType.Standard && <th>PL</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {articles
            .filter((article) => article.articleType === ArticleType.Product)
            .map((article) => (
              <tr
                role="button"
                key={article.rowNumber}
                onClick={() => onArticleClick(article)}
              >
                <td>
                  {!article.verified ? (
                    <X className="text-danger" />
                  ) : (
                    <Check className="text-success" />
                  )}
                </td>
                {showStockInfo &&
                  article.id &&
                  getStockStatusMessage(articleStockStatus[article.id]) && (
                    <td>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`tooltip-stock-${article.id}`}>
                            {article.id != null
                              ? getStockStatusMessage(
                                  articleStockStatus[article.id],
                                )
                              : ""}
                          </Tooltip>
                        }
                      >
                        <span>
                          <ChatText style={{ cursor: "pointer" }} />
                        </span>
                      </OverlayTrigger>
                    </td>
                  )}
                {orderPageType && orderPageType.articleTableArticleNumber && (
                  <td>{article.articleNumber}</td>
                )}
                {orderPageType && orderPageType.articleTableDescription && (
                  <td>{article.description}</td>
                )}
                {orderPageType && orderPageType.articleTableQuantity && (
                  <td>{article.quantity}</td>
                )}
                {clientType === ClientType.Fleet && (
                  <>
                    <td align="left">{article.stock ?? "-"}</td>
                  </>
                )}
                {orderPageType && orderPageType.articleTableSalesPrice && (
                  <td>{article.salesPrice}</td>
                )}
                {orderPageType &&
                  orderPageType.articleTableDiscount &&
                  (clientType === ClientType.Standard ||
                    clientType === ClientType.Fleet) && (
                    <td>{article.discount}</td>
                  )}
                {orderPageType && orderPageType.articleTablePurchasePrice && (
                  <td>
                    {article.purchasePrice}{" "}
                    {article.supplierId
                      ? suppliers.find((s) => s.id === article.supplierId)
                          ?.currency
                      : suppliers.find((s) => s.id === orderSupplierId)
                          ?.currency}
                  </td>
                )}
                {(clientType === ClientType.Catalog ||
                  (orderPageType && orderPageType.articleTableSupplierName)) && (
                  <td>{article.supplier?.name}</td>
                )}
                {orderPageType && orderPageType.articleTableIONumber && (
                  <td>{article.purchaseOrderNumber}</td>
                )}
                {orderPageType && orderPageType.articleTableComment && (
                  <td>{article.comment}</td>
                )}
                {clientType === ClientType.Fleet && (
                  <>
                    <td align="center">
                      {article.excludeFromWarehousePicking ? (
                        <span className="text-danger">Beställ ej</span>
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                  </>
                )}
                {orderPageType &&
                  orderPageType.articleTablePartUrl &&
                  clientType === ClientType.Standard && (
                    <td>
                      {!article.partUrl ? (
                        <span>-</span>
                      ) : (
                        <Link
                          onClick={(e) => e.stopPropagation()}
                          target="_blank"
                          to={article.partUrl ?? ""}
                        >
                          <Check className="text-success" />
                        </Link>
                      )}
                    </td>
                  )}
                <td align="center">
                  <Eye />
                </td>
              </tr>
            ))}
        </tbody>
      </Table>

      {articles.some((article) => article.articleType === ArticleType.Time) && (
        <>
          <h3>Reparationstider</h3>
          <Table responsive striped bordered hover>
            <thead>
              <tr>
                <th>
                  {allTimeVerified ? (
                    <CheckAll className="text-success" />
                  ) : (
                    <ExclamationTriangle className="text-danger" />
                  )}
                </th>
                {showStockInfo &&
                  articles.some(
                    (article) =>
                      article.id &&
                      getStockStatusMessage(articleStockStatus[article.id]),
                  ) && <th>Info</th>}
                <th>Art.nr</th>
                <th>Beskrivning</th>
                <th>Antal</th>
                <th>Försäljningspris</th>
                <th>Tid</th>
              </tr>
            </thead>
            <tbody>
              {articles
                .filter((article) => article.articleType === ArticleType.Time)
                .map((article) => (
                  <tr key={article.rowNumber}>
                    <td>
                      {!article.verified ? (
                        <X className="text-danger" />
                      ) : (
                        <Check className="text-success" />
                      )}
                    </td>
                    {showStockInfo &&
                      article.id &&
                      getStockStatusMessage(articleStockStatus[article.id]) && (
                        <td>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-stock-${article.id}`}>
                                {article.id &&
                                articleStockStatus[article.id] &&
                                typeof articleStockStatus[article.id] ===
                                  "object" &&
                                articleStockStatus[article.id].message
                                  ? articleStockStatus[article.id].message
                                  : article.id &&
                                    articleStockStatus[article.id] &&
                                    typeof articleStockStatus[article.id] ===
                                      "string"
                                  ? articleStockStatus[article.id]
                                  : ""}
                              </Tooltip>
                            }
                          >
                            <span>
                              <ChatText style={{ cursor: "pointer" }} />
                            </span>
                          </OverlayTrigger>
                        </td>
                      )}
                    <td>{article.articleNumber}</td>
                    <td>{article.description}</td>
                    <td>{article.quantity}</td>
                    <td>{article.salesPrice}</td>
                    <td>{article.time}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </>
      )}
    </>
  );
};

export default ArticlesTable;
